// @ts-strict-ignore
import Modal from 'antd/lib/modal/Modal';
import Form from 'antd/lib/form';
import { useForm } from 'antd/lib/form/Form';
import * as React from 'react';
import Input from 'antd/lib/input';
import Checkbox from 'antd/lib/checkbox';
import { UpdateReviewDetailsRequestForReleaseSteps, tryUpdateRelease } from '@/actions/release';
import { ReleaseUpdatable } from '@/actions/updatable';
import { useDispatch } from 'react-redux';
import { globalMessage } from '@/utils/globalMessage';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import { Nullable, StepRunComponentProps } from '@/typescriptHelpers';
import { ReviewAttachment, ReviewAttachmentUploadStatus, RunwayResponseType } from '@/types';
import ReleaseReviewAttachmentUpload from '@/components/ReleaseReviewAttachmentUpload';
export interface EditAppleReviewInfoModalValues {
    contactFirstName: string;
    contactLastName: string;
    contactEmail: string;
    contactPhone: string;
    notes?: string;
    demoAccountRequired: boolean;
    demoAccountName?: string;
    demoAccountPassword?: string;
}
export interface EditAppleReviewInfoModalProps {
    open: boolean;
    onCancel: () => void;
    initialValues: EditAppleReviewInfoModalValues;
    orgId: string;
    appId: string;
    releaseId: string;
    reviewId: string;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    reviewAttachments?: ReviewAttachment[];
}

export const EditAppleReviewInfoModal: React.FC<EditAppleReviewInfoModalProps> = ({
    open,
    onCancel,
    initialValues,
    releaseId,
    appId,
    orgId,
    reviewId,
    stepRunComponentProps,
    reviewAttachments,
}) => {
    const [form] = useForm<EditAppleReviewInfoModalValues>();

    const dispatch = useDispatch();

    const releaseReviewAttachment = reviewAttachments?.find(
        (attachment) => attachment.status !== ReviewAttachmentUploadStatus.Failed,
    );

    const [
        updateReviewDetailsStepRun,
        updateReviewDetailsIsLoadingStepRun,
        updateReviewDetailsResponseTypeStepRun,
        updateReviewDetailsErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.UpdateReviewDetails, stepRunComponentProps);

    const updateReviewDetailsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.UpdateReviewDetails, releaseId);
    const updateReviewDetailsSuccessReleaseStep = useUpdated(ReleaseUpdatable.UpdateReviewDetails, releaseId);
    const updateReviewDetailsErrorReleaseStep = useError(ReleaseUpdatable.UpdateReviewDetails, releaseId);

    const updateReviewDetailsLoading =
        stepRunComponentProps ? updateReviewDetailsIsLoadingStepRun : updateReviewDetailsLoadingReleaseStep;
    const updateReviewDetailsSuccess =
        stepRunComponentProps ?
            updateReviewDetailsResponseTypeStepRun === RunwayResponseType.Success
        :   updateReviewDetailsSuccessReleaseStep;
    const updateReviewDetailsError =
        stepRunComponentProps ? updateReviewDetailsErrorStepRun : updateReviewDetailsErrorReleaseStep;

    React.useEffect(() => {
        if (updateReviewDetailsSuccess) {
            globalMessage.success('Review details updated successfully!');
            onCancel();
        }
    }, [updateReviewDetailsSuccess]);

    React.useEffect(() => {
        if (updateReviewDetailsError) {
            globalMessage.error(updateReviewDetailsError);
        }
    }, [updateReviewDetailsError]);

    const handleOnSaveClicked = (values: EditAppleReviewInfoModalValues) => {
        const request: UpdateReviewDetailsRequestForReleaseSteps = {
            ...values,
            id: reviewId,
            appId,
            orgId,
            releaseId,
        };

        if (stepRunComponentProps) {
            updateReviewDetailsStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.UpdateReviewDetails, request, releaseId));
        }
    };

    const demoAccountRequired = Form.useWatch('demoAccountRequired', form);

    return (
        <Modal
            open={open}
            title={'Edit Apple review info'}
            okText={'Save'}
            okButtonProps={{ loading: updateReviewDetailsLoading }}
            onOk={() => form.submit()}
            onCancel={onCancel}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={() => {
                    form.validateFields().then((values) => {
                        handleOnSaveClicked(values);
                    });
                }}
                preserve={false}
                initialValues={initialValues}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        gap: 8,
                    }}
                >
                    <Form.Item
                        name="contactFirstName"
                        label="Contact first name"
                        rules={[
                            {
                                required: true,
                                message: 'This field is required',
                            },
                        ]}
                        style={{
                            flex: 1,
                        }}
                    >
                        <Input placeholder="First name" />
                    </Form.Item>
                    <Form.Item
                        label="Contact last name"
                        rules={[
                            {
                                required: true,
                                message: 'This field is required',
                            },
                        ]}
                        name="contactLastName"
                        style={{
                            flex: 1,
                        }}
                    >
                        <Input placeholder="Last name" />
                    </Form.Item>
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        gap: 8,
                    }}
                >
                    <Form.Item
                        label="Email"
                        name="contactEmail"
                        required
                        rules={[
                            {
                                type: 'email',
                                message: 'Please enter a valid email',
                            },
                        ]}
                        style={{
                            flex: 1,
                        }}
                    >
                        <Input placeholder="Email" type="email" />
                    </Form.Item>
                    <Form.Item
                        label="Phone"
                        name="contactPhone"
                        required
                        rules={[
                            {
                                pattern: /^(\+?\d{1,2}\s?)?(\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/,
                                message: 'Please enter a valid phone number',
                            },
                        ]}
                        style={{
                            flex: 1,
                        }}
                    >
                        <Input
                            placeholder="Phone"
                            type="tel"
                            pattern="^(\+?\d{1,2}\s?)?(\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$"
                        />
                    </Form.Item>
                </div>
                <Form.Item label="Notes" name="notes" rules={[{ max: 4000 }]}>
                    <Input.TextArea
                        autoSize={{
                            minRows: 5,
                        }}
                    />
                </Form.Item>
                <Form.Item name="demoAccountRequired" valuePropName="checked">
                    <Checkbox>Sign-in required</Checkbox>
                </Form.Item>
                {demoAccountRequired && (
                    <Form.Item label="Sign-in credentials" required>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                gap: 8,
                            }}
                        >
                            <Form.Item
                                name="demoAccountName"
                                required
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter a username and password',
                                    },
                                ]}
                                noStyle
                                style={{
                                    flex: 1,
                                }}
                            >
                                <Input placeholder="Username" />
                            </Form.Item>
                            <Form.Item
                                name="demoAccountPassword"
                                required
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter a username and password',
                                    },
                                ]}
                                noStyle
                                style={{
                                    flex: 1,
                                }}
                            >
                                <Input placeholder="Password" />
                            </Form.Item>
                        </div>
                    </Form.Item>
                )}
                <Form.Item label="Review attachment">
                    <ReleaseReviewAttachmentUpload
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        stepRunComponentProps={stepRunComponentProps}
                        currentAttachment={releaseReviewAttachment}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};
